import { useTranslation } from 'react-i18next';

// Components
import LiveSearchResultCard from '../LiveSearchResultCard/LiveSearchResultCard';
import Picto from '../../atoms/Picto/Picto';
import { Skeleton } from '../../atoms/Skeleton/Skeleton';

// Domain
import { mapLiveSearchResultToCardProps } from '../../../domain/live-search/liveSearchUseCase';

// Utils
import cn from '../../../utils/cn';

// Styles
import styles from './FormLiveSearchPanel.module.css';

const SKELETON_COUNT = 3;

/**
 * Vertical list of live search result cards on the right side of a form.
 * @param {object} props
 * @param {ApiLiveSearchResult[]} props.results
 * @param {boolean} props.isPending
 * @param {(resultId: string, actionIndex: number) => void} props.onActionClick
 * @param {boolean} [props.isActionPending]
 * @param {string[]} [props.highlightTerms=[]] - Query terms to highlight in cards
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
function FormLiveSearchPanel({
  results,
  isPending,
  onActionClick,
  isActionPending = false,
  highlightTerms = [],
  className,
}) {
  const { t } = useTranslation();
  const showEmpty = !isPending && results.length === 0;

  return (
    <aside
      className={cn([
        styles.panel,
        isActionPending && styles['is-auto-submit-pending'],
        className,
      ])}
      aria-label={t('form.liveSearch.panelLabel')}
      aria-busy={isActionPending}
    >
      <h2 className={styles.title}>
        {t('form.liveSearch.title')}
      </h2>

      <div className={styles.body}>
        {isPending && results.length === 0 ? (
          Array.from({ length: SKELETON_COUNT }, (_, index) => (
            <Skeleton
              key={`live-search-skeleton-${index}`}
              shape="rectangle"
              width="100%"
              height="150px"
            />
          ))
        ) : null}

        {results.map((result) => {
          const cardProps = mapLiveSearchResultToCardProps(result);
          return (
            <LiveSearchResultCard
              key={result.id}
              title={cardProps.title}
              subTitle={cardProps.subTitle}
              isActive={cardProps.isActive}
              rows={cardProps.rows}
              actions={cardProps.actions}
              highlightTerms={highlightTerms}
              onActionClick={(actionIndex) => onActionClick(result.id, actionIndex)}
              disabled={isActionPending}
            />
          );
        })}

        {showEmpty ? (
          <div className={styles.empty}>
            <p className={styles['empty-text']}>
              {t('form.liveSearch.empty')}
            </p>
            <Picto icon="search-normal" className={styles['empty-icon']} />
          </div>
        ) : null}
      </div>
    </aside>
  );
}

export default FormLiveSearchPanel;
